export type ToastMessage = {
  /**
   * Displayed after successful actions
   */
  success: string;
  /**
   * Error message when an action fails
   */
  error: string;
};

export interface IToastMessages {
  // Adjustments
  listAdjustments: ToastMessage;
  viewAdjustment: ToastMessage;
  createAdjustment: ToastMessage;
  updateAdjustment: ToastMessage;
  deleteAdjustment: ToastMessage;

  // Notes
  listNotes: ToastMessage;
  viewNote: ToastMessage;
  createNote: ToastMessage;
  updateNote: ToastMessage;
  deleteNote: ToastMessage;

  // Advances
  listAdvances: ToastMessage;
  viewAdvance: ToastMessage;
  createAdvance: ToastMessage;
  updateAdvance: ToastMessage;
  deleteAdvance: ToastMessage;

  // Assets
  listAssets: ToastMessage;
  viewAsset: ToastMessage;
  createAsset: ToastMessage;
  updateAsset: ToastMessage;
  deleteAsset: ToastMessage;

  // Companies
  listCompanies: ToastMessage;
  viewCompany: ToastMessage;
  createCompany: ToastMessage;
  updateCompany: ToastMessage;
  deleteCompany: ToastMessage;

  // Documents
  listDocuments: ToastMessage;
  viewDocument: ToastMessage;
  createDocument: ToastMessage;
  uploadDocument: ToastMessage;
  updateDocument: ToastMessage;
  deleteDocument: ToastMessage;

  // Lenders
  listLenders: ToastMessage;
  viewLender: ToastMessage;
  createLender: ToastMessage;
  updateLender: ToastMessage;
  deleteLender: ToastMessage;

  // Loans
  listLoans: ToastMessage;
  viewLoan: ToastMessage;
  createLoan: ToastMessage;
  updateLoan: ToastMessage;
  deleteLoan: ToastMessage;

  // Participations
  listParticipations: ToastMessage;

  // Payments
  listPayments: ToastMessage;
  viewPayment: ToastMessage;
  createPayment: ToastMessage;
  updatePayment: ToastMessage;
  deletePayment: ToastMessage;

  // Reports
  listReports: ToastMessage;
  viewReport: ToastMessage;
  createReport: ToastMessage;
  updateReport: ToastMessage;
  deleteReport: ToastMessage;

  // Statements
  listStatements: ToastMessage;
  viewStatement: ToastMessage;
  createStatement: ToastMessage;
  updateStatement: ToastMessage;
  deleteStatement: ToastMessage;

  // Transactions
  listTransactions: ToastMessage;
  viewTransaction: ToastMessage;
  createTransaction: ToastMessage;
  updateTransaction: ToastMessage;
  deleteTransaction: ToastMessage;

  // Users
  listUsers: ToastMessage;
  viewUser: ToastMessage;
  createUser: ToastMessage;
  updateUser: ToastMessage;
  deactivateUser: ToastMessage;
  activateUser: ToastMessage;
  changePassword: ToastMessage;
}

export const toastMessages: IToastMessages = {
  listAdjustments: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the adjustments list.',
  },
  viewAdjustment: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the adjustment.',
  },
  createAdjustment: {
    success: 'Adjustment Created',
    error: 'There was a problem creating the adjustment.',
  },
  updateAdjustment: {
    success: 'Adjustment Updated',
    error: 'There was a problem updating the adjustment.',
  },
  deleteAdjustment: {
    success: 'Adjustment Deleted',
    error: 'There was a problem deleting the adjustment.',
  },

  listNotes: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the notes list.',
  },
  viewNote: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the note.',
  },
  createNote: {
    success: 'Note Added',
    error: 'There was a problem adding the note.',
  },
  updateNote: {
    success: 'Note Updated',
    error: 'There was a problem updating the note.',
  },
  deleteNote: {
    success: 'Note Deleted',
    error: 'There was a problem deleting the note.',
  },

  listAdvances: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the advances list.',
  },
  viewAdvance: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the advance.',
  },
  createAdvance: {
    success: 'Advance Created',
    error: 'There was a problem creating the advance.',
  },
  updateAdvance: {
    success: 'Advance Updated',
    error: 'There was a problem updating the advance.',
  },
  deleteAdvance: {
    success: 'Advance Deleted',
    error: 'There was a problem deleting the advance.',
  },

  listAssets: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the assets list.',
  },
  viewAsset: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the asset.',
  },
  createAsset: {
    success: 'Asset Created',
    error: 'There was a problem creating the asset.',
  },
  updateAsset: {
    success: 'Asset Updated',
    error: 'There was a problem updating the asset.',
  },
  deleteAsset: {
    success: 'Asset Closed',
    error: 'There was a problem closing the asset.',
  },

  listCompanies: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the companies list.',
  },
  viewCompany: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the company.',
  },
  createCompany: {
    success: 'Company Created',
    error: 'There was a problem creating the company.',
  },
  updateCompany: {
    success: 'Company Updated',
    error: 'There was a problem updating the company.',
  },
  deleteCompany: {
    success: 'Company Deleted',
    error: 'There was a problem deleting the company.',
  },

  listDocuments: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the documents list.',
  },
  viewDocument: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the document.',
  },
  createDocument: {
    success: 'Document Created',
    error: 'There was a problem creating the document.',
  },
  uploadDocument: {
    success: 'Document Uploaded',
    error: 'There was a problem uploading the document.',
  },
  updateDocument: {
    success: 'Document Updated',
    error: 'There was a problem updating the document.',
  },
  deleteDocument: {
    success: 'Document Deleted',
    error: 'There was a problem deleting the document.',
  },

  listLenders: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the investors list.',
  },
  viewLender: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the investor.',
  },
  createLender: {
    success: 'Lender Created',
    error: 'There was a problem creating the investor.',
  },
  updateLender: {
    success: 'Lender Updated',
    error: 'There was a problem updating the investor.',
  },
  deleteLender: {
    success: 'Lender Deactivated',
    error: 'There was a problem deactivating the investor.',
  },

  listLoans: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the deals list.',
  },
  viewLoan: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the deal.',
  },
  createLoan: {
    success: 'Deal Created',
    error: 'There was a problem creating the deal.',
  },
  updateLoan: {
    success: 'Loan Updated',
    error: 'There was a problem updating the deal.',
  },
  deleteLoan: {
    success: 'Deal Deactivated',
    error: 'There was a problem deleting the deal.',
  },

  listParticipations: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the participations list.',
  },

  listPayments: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the payments list.',
  },
  viewPayment: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the payment.',
  },
  createPayment: {
    success: 'Payment Created',
    error: 'There was a problem creating the payment.',
  },
  updatePayment: {
    success: 'Payment Updated',
    error: 'There was a problem updating the payment.',
  },
  deletePayment: {
    success: 'Payment Deleted',
    error: 'There was a problem deleting the payment.',
  },

  listReports: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the reports list.',
  },
  viewReport: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the report.',
  },
  createReport: {
    success: 'Report Created',
    error: 'There was a problem creating the report.',
  },
  updateReport: {
    success: 'Report Updated',
    error: 'There was a problem updating the report.',
  },
  deleteReport: {
    success: 'Report Deleted',
    error: 'There was a problem deleting the report.',
  },

  listStatements: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the statements list.',
  },
  viewStatement: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the statement.',
  },
  createStatement: {
    success: 'Statement Created',
    error: 'There was a problem creating the statement.',
  },
  updateStatement: {
    success: 'Statement Updated',
    error: 'There was a problem updating the statement.',
  },
  deleteStatement: {
    success: 'Statement Deleted',
    error: 'There was a problem deleting the statement.',
  },

  listTransactions: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the transactions list.',
  },
  viewTransaction: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the transaction.',
  },
  createTransaction: {
    success: 'Transaction successfully recorded.',
    error: 'There was a problem recording the transaction.',
  },
  updateTransaction: {
    success: 'Transaction successfully updated.',
    error: 'There was a problem updating the transaction.',
  },
  deleteTransaction: {
    success: 'Transaction successfully removed.',
    error: 'There was a problem removing the transaction.',
  },

  listUsers: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the users list.',
  },
  viewUser: {
    success: 'Fetch successful!',
    error: 'There was a problem fetching the user.',
  },
  createUser: {
    success: 'User Created',
    error: 'There was a problem creating the user.',
  },
  updateUser: {
    success: 'User Updated',
    error: 'There was a problem updating the user.',
  },
  deactivateUser: {
    success: 'User Deactivated',
    error: 'There was a problem deactivating the user.',
  },
  activateUser: {
    success: 'User Activated',
    error: 'There was a problem activating the user.',
  },
  changePassword: {
    success: 'Password Changed',
    error: 'Error Changing Password',
  },
};
